// MA-288 kiosk-arming slice — a per-device "this device is armed for
// classroom kid hand-off on class X" primitive. Mirrors src/lib/deviceMode.ts's
// exact shape (localStorage, pure parse + getters/setters, useSyncExternalStore
// binding) — arming is device-level, like deviceMode, and per the item text
// ("kiosk arming persists per MA-03 deviceMode") is NOT cleared on sign-out or
// account deletion, matching deviceMode.ts's own precedent of never being
// cleared there either: a classroom iPad stays armed across whoever is
// currently signed into the teacher session on it.
//
// This slice does NOT read `class_enrollments` or attach kid identity to the
// armed class — that is the separate, not-yet-built `classroom-roster-identity`
// slice (see MA-288's `slices-remaining` ledger). Arming state has exactly one
// consumer today: a passive "Armed for {className}" caption on /play/who
// (src/routes/play.who.tsx), so the write path is real, not dead.
import { useSyncExternalStore } from "react";
import { clearDeviceMode, getDeviceModeRaw, setDeviceMode } from "@/lib/deviceMode";
// MA-553 — disarm must also purge the kiosk-tagged local registry entries
// this device accumulated while armed (see removeKioskTaggedChildren's own
// header in childrenStore.js for the scoping decision). No cycle risk:
// childrenStore.js imports only lib/engine/local{Attempt,Milestone}Store.ts,
// never anything under lib/teacher/.
import { removeKioskTaggedChildren } from "@/childrenStore.js";
// MA-925 (D-1012-29) — the roster prune, reached only through
// pruneKioskChildrenAfterRosterRead below.
import { pruneKioskChildrenAbsentFromRoster } from "@/childrenStore.js";

const KEY = "pw_kiosk_armed_class";

// MA-550/551 — arm/disarm now round-trips pw_device_mode too, not just
// pw_kiosk_armed_class. WHY: arming IS the "this is the class device"
// declaration (this file's own header above), and MA-550's fixed front-door
// fork means a device with pw_device_mode NEVER explicitly set now boots a
// signed-in teacher straight to /teacher — exactly wrong for a device that
// was JUST armed for kid hand-off. Stamping "kiosk" explicitly here closes
// that gap: an armed device always boots kid-first (resolveFrontDoorDestination's
// "explicit kiosk → /play, role ignored" row), no matter whose account is
// signed in or whether pw_device_mode had ever been set before. The prior
// raw value is stashed under PREARM_KEY BEFORE the stamp so disarm can put
// it back — a teacher who arms their OWN laptop by mistake gets their normal
// boot back on disarm, not a new "kiosk" default that arming introduced.
const PREARM_KEY = "pw_device_mode_prearm";
// Never a legal DeviceMode value, so it can't collide with a real stashed
// "personal"/"kiosk" — lets disarm tell "was never set" apart from "the
// prearm key itself doesn't exist" (e.g. a device armed before this slice
// shipped, then disarmed after) without a third ambiguous case to reason
// about.
const PREARM_UNSET_SENTINEL = "null";

export interface ArmedClass {
  classId: string;
  className: string;
  armedAt: string;
}

/** Pure parse — anything that isn't a well-formed {classId, className, armedAt}
 *  object with non-empty classId/className strings is "not armed" (null). */
export function parseArmedClass(raw: string | null | undefined): ArmedClass | null {
  if (!raw) return null;
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof obj !== "object" || obj === null) return null;
  const { classId, className, armedAt } = obj as Record<string, unknown>;
  if (typeof classId !== "string" || classId.length === 0) return null;
  if (typeof className !== "string" || className.length === 0) return null;
  if (typeof armedAt !== "string" || armedAt.length === 0) return null;
  return { classId, className, armedAt };
}

export function getArmedClass(): ArmedClass | null {
  if (typeof localStorage === "undefined") return null;
  return parseArmedClass(localStorage.getItem(KEY));
}

/** Arms (or re-arms, overwriting whatever class was previously armed) this
 *  device for the given class. Overwrite is deliberate — a device is armed
 *  for exactly one class at a time. */
export function armDeviceForClass(classId: string, className: string): void {
  if (typeof localStorage === "undefined") return;
  // MA-925 (D-TUX-6) — re-arming. A device that is ALREADY armed keeps the
  // pre-arm stash it took the first time (overwriting it here would record
  // "kiosk" as the "prior" mode, so a later disarm could never give the
  // teacher's laptop its normal boot back), and arming for a DIFFERENT class
  // runs the disarm purge first, so the old class's kiosk children leave the
  // device. Re-arming the SAME class purges nothing.
  const previous = getArmedClass();
  if (previous && previous.classId !== classId) {
    try {
      removeKioskTaggedChildren();
    } catch {
      /* private mode / quota — best-effort, same posture as disarmDevice */
    }
  }
  const value: ArmedClass = { classId, className, armedAt: new Date().toISOString() };
  try {
    localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    /* private mode / quota — arming is a device convenience, never required */
  }
  // MA-550/551 — stash the PRIOR raw device mode (read BEFORE the stamp
  // below changes it) so disarmDevice can restore it exactly. See this
  // file's header comment for why arming must also declare the device
  // kiosk, and PREARM_UNSET_SENTINEL's comment for why "null" is stored
  // literally rather than omitting the key.
  try {
    if (!previous) localStorage.setItem(PREARM_KEY, getDeviceModeRaw() ?? PREARM_UNSET_SENTINEL);
  } catch {
    /* private mode / quota — the stash is best-effort; arming itself (the
       kiosk stamp below) still takes effect even if the stash write fails,
       it just means disarm falls back to leaving mode at "kiosk" instead
       of restoring the true prior value. */
  }
  setDeviceMode("kiosk");
  refresh();
}

export function disarmDevice(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* no-op */
  }
  // MA-553 — close the accumulation half of the kiosk-registry-leak bug:
  // every kiosk-tapped roster tile this device ever ensured survives
  // disarm forever without this. Purge is unconditional (not gated on
  // whether KEY was actually removed above) so a disarm always leaves a
  // clean slate even if the KEY write failed (private mode / quota).
  try {
    removeKioskTaggedChildren();
  } catch {
    /* private mode / quota — best-effort, same posture as the rest of
       this function's localStorage writes. */
  }
  // MA-550/551 — restore whatever pw_device_mode was BEFORE this device was
  // armed. Round-trip contract: arm→disarm leaves pw_device_mode
  // byte-identical to before arming (a teacher who arms their own laptop by
  // mistake gets their normal boot back, not a lingering "kiosk" stamp).
  try {
    const prior = localStorage.getItem(PREARM_KEY);
    if (prior === PREARM_UNSET_SENTINEL) {
      clearDeviceMode();
    } else if (prior === "personal" || prior === "kiosk") {
      setDeviceMode(prior);
    }
    // prior === null (PREARM_KEY itself absent — e.g. a device armed before
    // this MA-550/551 slice shipped, then disarmed after it did) — nothing
    // to restore; leave pw_device_mode exactly as arming left it ("kiosk").
    localStorage.removeItem(PREARM_KEY);
  } catch {
    /* private mode / quota — the stash restore is best-effort */
  }
  refresh();
}

let snapshot: ArmedClass | null = getArmedClass();
const listeners = new Set<() => void>();

function refresh(): void {
  snapshot = getArmedClass();
  for (const l of listeners) l();
}

// MA-925 RED S1 — another tab arming, re-arming or disarming this device
// writes localStorage; the `storage` event tells THIS tab, so a stale tab
// never keeps acting for a class the device is no longer armed for.
let storageListening = false;
function onStorage(e: { key?: string | null }): void {
  if (e && (e.key === KEY || e.key === null)) refresh();
}

/** Subscribe to the armed class (this tab's arm/disarm AND other tabs' via
 *  the `storage` event). Exported for tests; React uses useArmedClass. */
export function subscribeArmedClass(cb: () => void): () => void {
  listeners.add(cb);
  if (!storageListening && typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("storage", onStorage as (e: StorageEvent) => void);
    storageListening = true;
  }
  return () => listeners.delete(cb);
}
const subscribe = subscribeArmedClass;

/** MA-925 (D-1012-29, RED round 1 M1/S1) — the ONLY path that prunes local
 *  kiosk children after a roster read. Deletes nothing unless ALL hold:
 *    - the read succeeded and returned at least one row (an RLS-scoped read by
 *      a signed-out or foreign session answers `200 []`, which is not the
 *      roster's truth);
 *    - the signed-in user is the class's own teacher (classTeacherId, read
 *      under RLS, equals signedInUserId; null on either side = cannot tell =
 *      no prune);
 *    - the device is STILL armed for the class that was read, re-read from
 *      localStorage right now (a stale tab never prunes another class).
 *  Returns the removed local ids, or the reason nothing was removed. */
export function pruneKioskChildrenAfterRosterRead(input: {
  classId: string;
  rosterEnrollmentIds: readonly string[];
  signedInUserId: string | null | undefined;
  classTeacherId: string | null | undefined;
}): { removed: string[]; skipped?: "empty" | "not-owner" | "not-armed-for-class" } {
  const { classId, rosterEnrollmentIds, signedInUserId, classTeacherId } = input;
  if (!Array.isArray(rosterEnrollmentIds) || rosterEnrollmentIds.length === 0) return { removed: [], skipped: "empty" };
  if (!signedInUserId || !classTeacherId || signedInUserId !== classTeacherId) return { removed: [], skipped: "not-owner" };
  if (getArmedClass()?.classId !== classId) return { removed: [], skipped: "not-armed-for-class" };
  return { removed: pruneKioskChildrenAbsentFromRoster([...rosterEnrollmentIds]) };
}

/** React binding for the teacher's arm/disarm control and /play/who's
 *  read-only caption. */
export function useArmedClass(): ArmedClass | null {
  return useSyncExternalStore(subscribe, () => snapshot, () => null);
}
